import AuthProvider from "../../../common/services/auth/AuthProvider/Index";
import { camelToDash, mergeErrors } from "../../../common/utils";

export interface CustomerAddress {
  name?: string;
  addressLine: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  countryCode: string;
  phone?: string;
}

export interface ClaimKitRequestBody {
  procedure_date: string;
  customer_address: CustomerAddress;
}

export interface UpsLabelResponse {
  trackingNumber: string;
  labelImage: string;
  referenceNumber: string;
}

export interface StatusChangeResponse {
  status: string;
  serial_number: string;
}

export interface AnalysisKitDataSchema {
  tracking_number: string | null;
  lot_number: string;
  serial_number: string;
  status: string;
  has_results?: boolean;
  results_id?: string;
  ups_label_generated?: boolean;
  ups_label?: {
    trackingNumber: string;
    labelImage?: string;
    referenceNumber: string;
  };
}

// ============================================================================
// Shared API Utilities
// ============================================================================

/**
 * Creates standard headers for authenticated API requests
 */
const createAuthHeaders = async (): Promise<HeadersInit> => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
});

/**
 * Handles API response errors consistently
 * @throws Error with appropriate message for 422 validation errors or generic failures
 */
const handleResponseError = async (response: Response): Promise<never> => {
  if (response.status === 422) {
    const result = await response.json();
    throw new Error(
      `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`
    );
  }
  throw new Error("Failed to load resource.");
};

/**
 * Processes a successful API response and returns the data
 */
const extractResponseData = async <T>(response: Response): Promise<T> => {
  const result = await response.json();
  return result.data;
};

/**
 * Makes an authenticated API request with standard error handling
 */
const apiRequest = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T | null> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: await createAuthHeaders(),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await extractResponseData<T>(response);
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

/**
 * Makes an authenticated API request that returns a boolean success status
 */
const apiRequestWithBooleanResult = async (
  url: string,
  options: RequestInit = {}
): Promise<boolean> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: await createAuthHeaders(),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return response.ok;
  } catch (error) {
    console.error("Error fetching data:", error);
    return false;
  }
};

// ============================================================================
// API Functions
// ============================================================================

/**
 * Changes the status of a semen analysis kit
 */
export const changeStatus = async (
  serialNumber: string,
  targetStatus: string
): Promise<StatusChangeResponse | null> => {
  const apiUrl = `${
    process.env.REACT_APP_API_BASE_URL
  }/v1/private/semen-analysis-kits/${serialNumber}/status/${camelToDash(
    targetStatus
  )}`;

  return apiRequest<StatusChangeResponse>(apiUrl, { method: "PATCH" });
};

/**
 * Fetches in-progress analysis kit data
 */
export const fetchInProgressData = async (
  abortSignal: AbortSignal | null = null
): Promise<AnalysisKitDataSchema | undefined> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/in-progress`;

  try {
    const response = await fetch(apiUrl, {
      headers: await createAuthHeaders(),
      method: "GET",
      signal: abortSignal,
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await extractResponseData<AnalysisKitDataSchema>(response);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Fetches results for a specific serial number
 */
export const fetchResults = async (serialNumber: string): Promise<unknown> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/results`;

  return apiRequest<unknown>(apiUrl, { method: "GET" });
};

/**
 * Generates a UPS return label for a kit
 */
export const generateUpsReturnLabel = async (
  serialNumber: string,
  customerAddress: CustomerAddress
): Promise<UpsLabelResponse> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/ups-return-label`;

  const response = await fetch(apiUrl, {
    headers: await createAuthHeaders(),
    method: "POST",
    body: JSON.stringify({
      customer_address: customerAddress,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to generate UPS label");
  }

  const result = await response.json();
  return result.data;
};

/**
 * Claims a kit with the given serial number
 */
export const claimKit = async (
  serialNumber: string,
  requestBody: ClaimKitRequestBody
): Promise<Response> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/status/claimed`;

  return fetch(apiUrl, {
    method: "PATCH",
    headers: await createAuthHeaders(),
    body: JSON.stringify(requestBody),
  });
};

/**
 * Updates the tracking number for a kit
 */
export const updateTrackingNumber = async (
  serialNumber: string,
  trackingNumber: string
): Promise<Response> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/tracking-number`;

  return fetch(apiUrl, {
    headers: await createAuthHeaders(),
    method: "PATCH",
    body: JSON.stringify({
      tracking_number: trackingNumber,
    }),
  });
};

/**
 * Saves sample collection timestamp
 */
export const saveSampleCollectionTimestamp = async (
  serialNumber: string,
  timestamp: Date
): Promise<boolean> => {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/sample-collection-timestamp`;

  return apiRequestWithBooleanResult(apiUrl, {
    method: "PATCH",
    body: JSON.stringify({
      sample_collection_timestamp: timestamp.toISOString(),
    }),
  });
};
